import fetchData from './fetchData';

/**
 * Fetchers for /ft-lifetime-value (Finanztrends Customer Lifetime Value).
 * Backend: node-app/src/routes/ft-lifetime-value.ts — responses use the
 * { success, data } envelope, so both fetchers run with expectArray=false and
 * unwrap `.data` here.
 */

export interface LtvCohort {
  month: string;
  signups: number;
  ftOrders: number;
  ftRev: number;
  ftPerSignup: number;
  dnvReached: number;
  dnvReachedShare: number;
  dnvClosed: number;
  dnvClosedShare: number | null;
  dnvOrders: number;
  dnvPlx: number;
  dnvVnr: number;
  dnvLeads: number;
  dnvRev: number;
  dnvPerSignup: number;
  dnvPerReached: number | null;
  kmeReached: number;
  kmeReachedShare: number;
  kmeClosed: number;
  kmeClosedShare: number | null;
  kmeOrders: number;
  kmePlx: number;
  kmeVnr: number;
  kmeLeads: number;
  kmeRev: number;
  kmePerSignup: number;
  kmePerReached: number | null;
  totalRev: number;
  totalPerSignup: number;
}

export interface LtvStageMonth {
  month: string;
  entrants: number;
  closed: number;
  closedShare: number;
  orders: number;
  plx: number;
  vnr: number;
  leads: number;
  rev: number;
  perEntrant: number;
}

export interface LtvKpis {
  signups: number;
  ftRev: number;
  dnvRev: number;
  kmeRev: number;
  totalRev: number;
  ftPerSignup: number;
  dnvPerSignup: number;
  kmePerSignup: number;
  totalPerSignup: number;
  dnvReached: number;
  dnvReachedShare: number;
  kmeReached: number;
  kmeReachedShare: number;
}

export interface LtvSummary {
  params: {
    startDate: string;
    endDate: string;
    days: number;
    aboStatuses: string[];
    sources?: string;
  };
  days: number;
  today: string;
  journey: LtvCohort[];
  stages: { dnv: LtvStageMonth[]; kme: LtvStageMonth[] };
  kpis: LtvKpis;
}

export interface LtvEmailRow {
  email: string;
  signupDate: string;
  dnvDate: string | null;
  kmeDate: string | null;
  kmeUnsubTs: string | null;
  ftOrders: number;
  ftRev: number;
  dnvOrders: number;
  dnvPlx: number;
  dnvVnr: number;
  dnvLeads: number;
  kmeOrders: number;
  kmePlx: number;
  kmeVnr: number;
  kmeLeads: number;
  dnvRev: number;
  kmeRev: number;
  totalRev: number;
}

export interface LtvEmails {
  month: string;
  days: number;
  today: string;
  totalCount: number;
  truncated: boolean;
  rows: LtvEmailRow[];
}

export interface LtvQuery {
  startDate: string;
  endDate: string;
  days: number;
  aboStatuses: string[];
  sources: string[];
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

function buildParams(q: LtvQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {
    startDate: q.startDate,
    endDate: q.endDate,
    days: q.days,
  };
  if (q.aboStatuses.length > 0) params.aboStatuses = q.aboStatuses.join(',');
  if (q.sources.length > 0) params.sources = q.sources.join(',');
  return params;
}

async function unwrap<T>(p: Promise<Envelope<T> | Envelope<T>[]>, endpoint: string): Promise<T> {
  const res = (await p) as Envelope<T>;
  if (!res || res.success !== true || res.data === undefined) {
    throw new Error(res?.error || `Unexpected response from ${endpoint}`);
  }
  return res.data;
}

export const fetchLtvSummary = (q: LtvQuery): Promise<LtvSummary> =>
  unwrap(
    fetchData<Envelope<LtvSummary>>('/api/ft-lifetime-value/summary', buildParams(q), false),
    '/api/ft-lifetime-value/summary',
  );

export const fetchLtvEmails = (q: LtvQuery, month: string): Promise<LtvEmails> =>
  unwrap(
    fetchData<Envelope<LtvEmails>>(
      '/api/ft-lifetime-value/emails',
      { ...buildParams(q), month },
      false,
    ),
    '/api/ft-lifetime-value/emails',
  );
